/** Daily-cap ramp: rows of { from: yyyy-mm-dd, cap } in the business timezone. */

import { DAY_KEYS, type DayKey, type RampRow } from './types'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export function isIsoDate(s: string): boolean {
  if (!ISO_DATE.test(s)) return false
  const d = new Date(`${s}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s
}

export function sortRamp(rows: readonly RampRow[]): RampRow[] {
  return [...rows].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0))
}

/** Normalise whatever the database returned into sorted, typed rows. */
export function parseRamp(raw: unknown): RampRow[] {
  if (!Array.isArray(raw)) return []
  const rows: RampRow[] = []
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue
    const o = r as Record<string, unknown>
    const from = typeof o.from === 'string' ? o.from : ''
    const cap = Number(o.cap)
    if (isIsoDate(from) && Number.isInteger(cap) && cap >= 0) rows.push({ from, cap })
  }
  return sortRamp(rows)
}

/** Human-readable problem with a ramp, or null when it can be saved. */
export function validateRamp(rows: readonly RampRow[]): string | null {
  const seen = new Set<string>()
  for (const r of rows) {
    if (!isIsoDate(r.from)) return `"${r.from || '(empty)'}" is not a date. Use yyyy-mm-dd.`
    if (!Number.isInteger(r.cap) || r.cap < 0 || r.cap > 100000) {
      return `Cap for ${r.from} must be a whole number from 0 to 100000.`
    }
    if (seen.has(r.from)) return `${r.from} appears twice.`
    seen.add(r.from)
  }
  return null
}

/** Mirrors program_status: latest row on or before the date, else the flat cap. */
export function capForDate(ramp: readonly RampRow[], dailyCap: number, isoDate: string): number {
  let cap = dailyCap
  for (const r of sortRamp(ramp)) {
    if (r.from <= isoDate) cap = r.cap
  }
  return cap
}

/** yyyy-mm-dd of `now` in an IANA timezone. */
export function localDateIn(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

export function localDayIn(timeZone: string, now: Date = new Date()): DayKey {
  const wd = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' })
    .format(now)
    .toLowerCase()
  return DAY_KEYS.find((d) => wd.startsWith(d)) ?? 'mon'
}

export function isValidTimeZone(tz: string): boolean {
  if (!tz.trim()) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}
